import React, { useState } from "react";
import styled from "styled-components/macro";
import { MdCloudUpload, MdClose } from "react-icons/md";

/**
 * A single upload target: clicking anywhere on it opens the file picker, and a
 * file can equally be dragged onto it. Format and size are checked here, before
 * anything is transferred, so a file that was never going to be accepted is
 * refused immediately rather than after a wait.
 */

const Zone = styled.div`
  position: relative;
  width: 100%;
  height: 145px;
  border: 2px dashed ${(props) => (props.dragging ? "#3298dc" : "#d3d3d3")};
  background: ${(props) => (props.dragging ? "rgba(50, 152, 220, 0.08)" : "transparent")};
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${(props) => (props.disabled ? "#d3d3d3" : "#808080")};
  }
`;

const ZoneInfo = styled.div`
  position: absolute;
  top: 50%;
  width: 100%;
  transform: translate(0, -50%);
  text-align: center;
  pointer-events: none;

  svg {
    font-size: 2.5rem;
    opacity: 0.5;
  }
`;

const ZoneTitle = styled.div`
  font-size: 1.1rem;
  font-family: "Source Sans Pro Semibold";

  span {
    color: #3298dc;
  }
`;

const ZoneSubtitle = styled.div`
  font-size: 0.85rem;
  opacity: 0.6;
`;

const HiddenInput = styled.input`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
`;

const ErrorList = styled.div`
  margin-top: 0.5rem;

  > div {
    display: flex;
    align-items: center;
    color: #c0392b;
    font-size: 0.9rem;
    padding: 0.25rem 0;
  }
`;

const DismissError = styled.button`
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  display: flex;
  align-items: center;
`;

export const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const kb = bytes / 1024;
  if (kb > 800) {
    return `${Math.round((kb / 1024) * 100) / 100} MB`;
  }
  return `${Math.round(kb * 100) / 100} KB`;
};

const DropZone = ({
  accept,
  acceptDescription,
  maxBytes,
  maxDescription,
  disabled,
  onFileSelected,
}) => {
  const [dragging, setDragging] = useState(false);
  const [errors, setErrors] = useState([]);
  // Remounting the input after a selection lets the same file be chosen again
  // after it has been removed, which a browser otherwise suppresses.
  const [inputKey, setInputKey] = useState(0);

  const addError = (message) => setErrors((prev) => [...prev, message]);

  const dismissError = (index) =>
    setErrors((prev) => prev.filter((_, i) => i !== index));

  const validateAndSelect = (file) => {
    if (!file) return;

    const extension = file.name.includes(".")
      ? `.${file.name.split(".").pop().toLowerCase()}`
      : "";

    if (!accept.includes(extension)) {
      addError(`${file.name} is not a ${acceptDescription} file.`);
      return;
    }

    if (maxBytes && file.size > maxBytes) {
      addError(`${file.name} is larger than ${maxDescription}.`);
      return;
    }

    onFileSelected(file);
    setInputKey((prev) => prev + 1);
  };

  const handleChange = (e) => {
    setDragging(false);
    const [file] = e.target.files ?? [];
    validateAndSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const [file] = e.dataTransfer?.files ?? [];
    validateAndSelect(file);
  };

  return (
    <div>
      <Zone
        dragging={dragging}
        disabled={disabled}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <ZoneInfo>
          <MdCloudUpload />
          <ZoneTitle>
            <span>Upload a file</span> or drag and drop
          </ZoneTitle>
          <ZoneSubtitle>
            {acceptDescription.toUpperCase()}
            {maxDescription ? ` (max ${maxDescription})` : ""}
          </ZoneSubtitle>
        </ZoneInfo>
        <HiddenInput
          key={inputKey}
          type="file"
          accept={accept.join(",")}
          disabled={disabled}
          onChange={handleChange}
        />
      </Zone>

      {errors.length ? (
        <ErrorList>
          {errors.map((message, i) => (
            <div key={`${message}-${i}`}>
              {message}
              <DismissError type="button" onClick={() => dismissError(i)}>
                <MdClose />
              </DismissError>
            </div>
          ))}
        </ErrorList>
      ) : null}
    </div>
  );
};

export default DropZone;
